import React from "react";

import { RouteObject } from "react-router";

import { Lazy } from "@shared/components/ui/lazy";

const Admin = Lazy(() => import("@shared/features/admin/pages/admin"), "Admin");
const AdminAccountDeletions = Lazy(
  () => import("@shared/features/admin/pages/admin-account-deletions"),
  "AdminAccountDeletions"
);
const AdminAudit = Lazy(
  () => import("@shared/features/admin/pages/admin-audit"),
  "AdminAudit"
);
const AdminUserFile = Lazy(
  () => import("@shared/features/admin/pages/admin-user-file"),
  "AdminUserFile"
);
const AdminProfessionals = Lazy(
  () => import("@shared/features/admin/pages/admin-professionals"),
  "AdminProfessionals"
);
const AdminProfessional = Lazy(
  () => import("@shared/features/admin/pages/admin-professional"),
  "AdminProfessional"
);
const AdminAppointments = Lazy(
  () => import("@shared/features/admin/pages/admin-appointments"),
  "AdminAppointments"
);
const AdminDispatch = Lazy(
  () => import("@shared/features/admin/pages/admin-dispatch"),
  "AdminDispatch"
);
const AdminDispatchDetail = Lazy(
  () => import("@shared/features/admin/pages/admin-dispatch-detail"),
  "AdminDispatchDetail"
);
const AdminPayments = Lazy(
  () => import("@shared/features/admin/pages/admin-payments"),
  "AdminPayments"
);
const AdminHealth = Lazy(
  () => import("@shared/features/admin/pages/admin-health"),
  "AdminHealth"
);
const AdminIntegrations = Lazy(
  () => import("@shared/features/admin/pages/admin-integrations"),
  "AdminIntegrations"
);
const AdminNotifications = Lazy(
  () => import("@shared/features/admin/pages/admin-notifications"),
  "AdminNotifications"
);
const AdminNotFound = Lazy(
  () => import("@shared/features/admin/pages/admin-not-found"),
  "AdminNotFound"
);
const AdminOrganization = Lazy(
  () => import("@shared/features/admin/pages/admin-organization"),
  "AdminOrganization"
);
const AdminOrganizations = Lazy(
  () => import("@shared/features/admin/pages/admin-organizations"),
  "AdminOrganizations"
);
const AdminPendingOrganizations = Lazy(
  () => import("@shared/features/admin/pages/admin-pending-organizations"),
  "AdminPendingOrganizations"
);
const AdminProductCreate = Lazy(
  () => import("@shared/features/admin/pages/admin-product-create"),
  "AdminProductCreate"
);
const AdminProductEdit = Lazy(
  () => import("@shared/features/admin/pages/admin-product-edit"),
  "AdminProductEdit"
);
const AdminProducts = Lazy(
  () => import("@shared/features/admin/pages/admin-product"),
  "AdminProducts"
);
const AdminSettings = Lazy(
  () => import("@shared/features/admin/pages/admin-profile"),
  "AdminSettings"
);
const AdminShadowReport = Lazy(
  () => import("@shared/features/admin/pages/admin-shadow-report"),
  "AdminShadowReport"
);
const AdminSupplyRequestDetail = Lazy(
  () => import("@shared/features/admin/pages/admin-supply-request-detail"),
  "AdminSupplyRequestDetail"
);
const AdminSupplyRequests = Lazy(
  () => import("@shared/features/admin/pages/admin-supply-requests"),
  "AdminSupplyRequests"
);
const AdminUsers = Lazy(
  () => import("@shared/features/admin/pages/admin-users"),
  "AdminUsers"
);
const AdminFeedbackDetails = Lazy(
  () => import("@shared/features/feedback/pages/admin-feedback-details"),
  "AdminFeedbackDetails"
);
const AdminFeedbackList = Lazy(
  () => import("@shared/features/feedback/pages/admin-feedback-list"),
  "AdminFeedbackList"
);
const ResultCheckup = Lazy(
  () => import("@shared/features/patient-check-up/result-checkup"),
  "ResultCheckup"
);
const ResultCheckupForm = Lazy(
  () => import("@shared/features/patient-check-up/result-checkup-form"),
  "ResultCheckupForm"
);

/** Children of /app in the admin app, rendered inside its layout. */
export const getAdminRoutes = (): RouteObject[] => {
  return [
    { index: true, element: <Admin /> },

    // Comptes
    {
      path: "users",
      children: [
        { index: true, element: <AdminUsers tab="list" /> },
        { path: "statistics", element: <AdminUsers tab="statistics" /> },
        { path: ":id", element: <AdminUserFile /> },
      ],
    },
    {
      path: "organizations",
      children: [
        { index: true, element: <AdminOrganizations tab="list" /> },
        { path: "decisions", element: <AdminOrganizations tab="decisions" /> },
        { path: ":id", element: <AdminOrganization /> },
      ],
    },
    {
      path: "professionals",
      children: [
        { index: true, element: <AdminProfessionals /> },
        { path: ":type/:id", element: <AdminProfessional /> },
      ],
    },
    { path: "pending-organizations", element: <AdminPendingOrganizations /> },

    // Activité
    {
      path: "appointments",
      children: [
        { index: true, element: <AdminAppointments tab="statistics" /> },
        { path: "list", element: <AdminAppointments tab="list" /> },
      ],
    },
    { path: "payments", element: <AdminPayments /> },
    {
      path: "feedbacks",
      children: [
        { index: true, element: <AdminFeedbackList /> },
        { path: ":id", element: <AdminFeedbackDetails /> },
      ],
    },
    {
      path: "patient-check-result",
      children: [
        { index: true, element: <ResultCheckup /> },
        { path: ":id", element: <ResultCheckupForm /> },
      ],
    },

    // Logistique
    {
      path: "dispatch",
      children: [
        { index: true, element: <AdminDispatch /> },
        { path: ":id", element: <AdminDispatchDetail /> },
      ],
    },
    { path: "products", element: <AdminProducts /> },
    { path: "products/new", element: <AdminProductCreate /> },
    { path: "products/edit/:id", element: <AdminProductEdit /> },
    { path: "supply-requests", element: <AdminSupplyRequests /> },
    { path: "supply-requests/:id", element: <AdminSupplyRequestDetail /> },

    // Système
    { path: "shadow-report", element: <AdminShadowReport /> },
    {
      path: "account-deletions",
      children: [
        { index: true, element: <AdminAccountDeletions tab="unfinished" /> },
        { path: "history", element: <AdminAccountDeletions tab="history" /> },
      ],
    },
    { path: "audit", element: <AdminAudit /> },
    { path: "integrations", element: <AdminIntegrations /> },
    { path: "notifications", element: <AdminNotifications /> },
    { path: "health", element: <AdminHealth /> },

    { path: "user/settings", element: <AdminSettings /> },
    { path: "*", element: <AdminNotFound /> },
  ];
};
